import Image from 'next/image';
import { Dispatch, SetStateAction } from 'react';

import { CapitalRequestInput } from '@scaleup/models';
import { Button } from '../../../ui';

export const CapitalTypes = ({
  capitalRequestInput,
  setCurrentStep,
  handleChange,
  isLoading,
}: {
  setCurrentStep: Dispatch<
    SetStateAction<
      | 'REQUIREMENTS'
      | 'INTRODUCTION'
      | 'CAPITAL TYPES'
      | 'GROWTH PLAN'
      | 'FINANCIALS'
      | 'OWNERSHIP'
      | 'EBITDA'
      | 'AMOUNT NEEDED'
      | 'BREAKDOWN'
      | 'REVIEW'
      | 'CONFIRMATION'
    >
  >;
  capitalRequestInput: CapitalRequestInput;
  handleChange: (key: string, value: string | string[]) => void;
  isLoading: boolean;
}) => {
  const addCapitalType = (type: string) => {
    capitalRequestInput.capitalTypes.push(type);
    handleChange('capitalTypes', capitalRequestInput.capitalTypes);
  };

  const removeCapitalType = (type: string) => {
    handleChange(
      'capitalTypes',
      capitalRequestInput.capitalTypes.filter(
        (capitalType) => capitalType !== type
      )
    );
  };

  return (
    <div className="flex gap-8">
      <div className="flex flex-col gap-4 flex-1">
        <div className="flex flex-col gap-2">
          <span className="text-2xl font-bold">Capital Types</span>
          <span className="text-base font-normal">
            What types of capital would you be open to exploring? Select all
            that apply.
          </span>
        </div>
        <div className="flex flex-col gap-2">
          <div>
            <input
              id="majority"
              name="majority"
              type="checkbox"
              className="hidden peer"
              defaultChecked={capitalRequestInput.capitalTypes.includes(
                'Leveraged Buyout / Majority (Control)'
              )}
              onChange={(e) =>
                e.target.checked
                  ? addCapitalType('Leveraged Buyout / Majority (Control)')
                  : removeCapitalType('Leveraged Buyout / Majority (Control)')
              }
            />
            <label
              htmlFor="majority"
              className="flex justify-between items-center gap-2 border-gray-200 border rounded-2xl p-4 peer-checked:bg-primary-25 group"
            >
              <div className="flex gap-2 items-center">
                <Image
                  className="rounded-full bg-primary-100 p-2 h-8"
                  src="/icons/note.svg"
                  alt="Bank Note"
                  width={32}
                  height={32}
                />
                <div className="flex flex-col">
                  <span className="font-bold">
                    Leveraged Buyout / Majority (Control)
                  </span>
                  <span className="text-sm font-normal">
                    This is a description of the capital type.
                  </span>
                </div>
              </div>
            </label>
          </div>
          <div>
            <input
              id="minority"
              name="majority"
              type="checkbox"
              className="hidden peer"
              defaultChecked={capitalRequestInput.capitalTypes.includes(
                'Growth Equity / Minority (Non-control)'
              )}
              onChange={(e) =>
                e.target.checked
                  ? addCapitalType('Growth Equity / Minority (Non-control)')
                  : removeCapitalType('Growth Equity / Minority (Non-control)')
              }
            />
            <label
              htmlFor="minority"
              className="flex justify-between items-center gap-2 border-gray-200 border rounded-2xl p-4 peer-checked:bg-primary-25 group"
            >
              <div className="flex gap-2 items-center">
                <Image
                  className="rounded-full bg-primary-100 p-2 h-8"
                  src="/icons/note.svg"
                  alt="Bank Note"
                  width={32}
                  height={32}
                />
                <div className="flex flex-col">
                  <span className="font-bold">
                    Growth Equity / Minority (Non-control)
                  </span>
                  <span className="text-sm font-normal">
                    This is a description of the capital type.
                  </span>
                </div>
              </div>
            </label>
          </div>
          <div>
            <input
              id="junior"
              name="junior"
              type="checkbox"
              className="hidden peer"
              defaultChecked={capitalRequestInput.capitalTypes.includes(
                'Junior Debt'
              )}
              onChange={(e) =>
                e.target.checked
                  ? addCapitalType('Junior Debt')
                  : removeCapitalType('Junior Debt')
              }
            />
            <label
              htmlFor="junior"
              className="flex justify-between items-center gap-2 border-gray-200 border rounded-2xl p-4 peer-checked:bg-primary-25 group"
            >
              <div className="flex gap-2 items-center">
                <Image
                  className="rounded-full bg-primary-100 p-2 h-8"
                  src="/icons/note.svg"
                  alt="Bank Note"
                  width={32}
                  height={32}
                />
                <div className="flex flex-col">
                  <span className="font-bold">
                    Junior Debt (i.e., Subordinated Debt or Mezzanine Debt)
                  </span>
                  <span className="text-sm font-normal">
                    This is a description of the capital type.
                  </span>
                </div>
              </div>
            </label>
          </div>
          <div>
            <input
              id="senior"
              name="senior"
              type="checkbox"
              className="hidden peer"
              defaultChecked={capitalRequestInput.capitalTypes.includes(
                'Senior Debt'
              )}
              onChange={(e) =>
                e.target.checked
                  ? addCapitalType('Senior Debt')
                  : removeCapitalType('Senior Debt')
              }
            />
            <label
              htmlFor="senior"
              className="flex justify-between items-center gap-2 border-gray-200 border rounded-2xl p-4 peer-checked:bg-primary-25 group"
            >
              <div className="flex gap-2 items-center">
                <Image
                  className="rounded-full bg-primary-100 p-2 h-8"
                  src="/icons/note.svg"
                  alt="Bank Note"
                  width={32}
                  height={32}
                />
                <div className="flex flex-col">
                  <span className="font-bold">
                    Senior Debt (i.e., Term Loans)
                  </span>
                  <span className="text-sm font-normal">
                    This is a description of the capital type.
                  </span>
                </div>
              </div>
            </label>
          </div>
          <div>
            <input
              id="credit"
              name="credit"
              type="checkbox"
              className="hidden peer"
              defaultChecked={capitalRequestInput.capitalTypes.includes(
                'Lines of credit'
              )}
              onChange={(e) =>
                e.target.checked
                  ? addCapitalType('Lines of credit')
                  : removeCapitalType('Lines of credit')
              }
            />
            <label
              htmlFor="credit"
              className="flex justify-between items-center gap-2 border-gray-200 border rounded-2xl p-4 peer-checked:bg-primary-25 group"
            >
              <div className="flex gap-2 items-center">
                <Image
                  className="rounded-full bg-primary-100 p-2 h-8"
                  src="/icons/note.svg"
                  alt="Bank Note"
                  width={32}
                  height={32}
                />
                <div className="flex flex-col">
                  <span className="font-bold">Lines of credit</span>
                  <span className="text-sm font-normal">
                    This is a description of the capital type.
                  </span>
                </div>
              </div>
            </label>
          </div>
          <div>
            <input
              id="specialty"
              name="specialty"
              type="checkbox"
              className="hidden peer"
              defaultChecked={capitalRequestInput.capitalTypes.includes(
                'Specialty'
              )}
              onChange={(e) =>
                e.target.checked
                  ? addCapitalType('Specialty')
                  : removeCapitalType('Specialty')
              }
            />
            <label
              htmlFor="specialty"
              className="flex justify-between items-center gap-2 border-gray-200 border rounded-2xl p-4 peer-checked:bg-primary-25 group"
            >
              <div className="flex gap-2 items-center">
                <Image
                  className="rounded-full bg-primary-100 p-2 h-8"
                  src="/icons/note.svg"
                  alt="Bank Note"
                  width={32}
                  height={32}
                />
                <div className="flex flex-col">
                  <span className="font-bold">
                    Specialty (Equip, Factoring, et al)
                  </span>
                  <span className="text-sm font-normal">
                    This is a description of the capital type.
                  </span>
                </div>
              </div>
            </label>
          </div>
        </div>
        <div className="flex gap-3">
          <div>
            <Button
              text="Back"
              variant={isLoading ? 'disabled' : 'secondary'}
              onClick={() => setCurrentStep('INTRODUCTION')}
            />
          </div>
          <div>
            <Button
              text="Continue"
              variant={isLoading ? 'disabled' : 'primary'}
              suffixIcon="/icons/arrow_right.svg"
              onClick={() => setCurrentStep('GROWTH PLAN')}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-1 justify-center">
        <Image
          src="/create_cr.png"
          alt="Supplier Request Image"
          priority
          width={512}
          height={584}
          style={{ objectFit: 'contain' }}
        />
      </div>
    </div>
  );
};
